import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';
import { Icons } from '../lib/Icons';
import {
  generateWorksheet,
  generateProgressSheet,
  generateSensoryStory,
  generateVisualSupport,
  getDownloadTypeName,
} from '../api/downloadableResources';

/**
 * Modal for customizing and generating downloadable resources
 * Uses consistent brand styling with SVG icons - no emojis
 */
const GenerateResourceModal = ({
  isOpen,
  onClose,
  downloadType,
  activity,
  interventionData = null,
  studentNames = [],
  targets = [],
  onGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [formData, setFormData] = useState({
    // Worksheet options
    sessionNumber: 1,
    studentName: '',
    communicationLevel: 'semi-formal',
    customInstructions: '',
    // Progress sheet options
    weekCount: 6,
    title: '',
    // Sensory story options
    theme: '',
    sensoryElements: '',
    format: 'print',
    // Visual support options
    supportType: 'comic_strip',
    scenario: '',
    studentContext: '',
    panelCount: 4,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    
    try {
      let result;
      
      switch (downloadType) {
        case 'worksheet':
          result = await generateWorksheet({
            activitySlug: activity.slug || activity.id,
            sessionNumber: parseInt(formData.sessionNumber) || 1,
            studentName: formData.studentName || undefined,
            communicationLevel: formData.communicationLevel,
            customInstructions: formData.customInstructions || undefined,
          });
          break;
          
        case 'progress_sheet':
          result = await generateProgressSheet({
            interventionBlockId: interventionData?.id,
            activitySlug: activity.slug || activity.id,
            targets: targets.length > 0 ? targets : [
              { id: '1', text: 'Target 1' },
              { id: '2', text: 'Target 2' },
              { id: '3', text: 'Target 3' },
            ],
            studentNames: studentNames.length > 0 ? studentNames : [],
            weekCount: parseInt(formData.weekCount) || 6,
            title: formData.title || `${activity.title} - Progress Tracker`,
          });
          break;
          
        case 'story':
          if (!formData.theme) {
            toast.error('Please enter a story theme');
            setIsGenerating(false);
            return;
          }
          result = await generateSensoryStory({
            theme: formData.theme,
            sensoryElements: formData.sensoryElements
              ? formData.sensoryElements.split(',').map(s => s.trim())
              : ['touch', 'sound', 'sight'],
            communicationLevel: formData.communicationLevel,
            format: formData.format,
            studentName: formData.studentName || undefined,
          });
          break;
          
        case 'visual_support':
          if (!formData.scenario) {
            toast.error('Please enter a scenario description');
            setIsGenerating(false);
            return;
          }
          result = await generateVisualSupport({
            supportType: formData.supportType,
            scenario: formData.scenario,
            studentContext: formData.studentContext || undefined,
            panelCount: parseInt(formData.panelCount) || 4,
          });
          break;
          
        default:
          throw new Error(`Unknown download type: ${downloadType}`);
      }

      toast.success('Resource generated successfully!');
      
      // Open download in new tab
      window.open(result.downloadUrl, '_blank');
      
      // Notify parent
      if (onGenerated) {
        onGenerated(result);
      }
      
      onClose();
    } catch (error) {
      console.error('Error generating resource:', error);
      toast.error(error.message || 'Failed to generate resource');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderFormFields = () => {
    switch (downloadType) {
      case 'worksheet':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Number
              </label>
              <input
                type="number"
                name="sessionNumber"
                value={formData.sessionNumber}
                onChange={handleInputChange}
                min="1"
                max="20"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student Name (optional)
              </label>
              <input
                type="text"
                name="studentName"
                value={formData.studentName}
                onChange={handleInputChange}
                placeholder="Leave blank for generic worksheet"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Communication Level
              </label>
              <select
                name="communicationLevel"
                value={formData.communicationLevel}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="pre-formal">Pre-formal (symbols, minimal text)</option>
                <option value="semi-formal">Semi-formal (simple sentences)</option>
                <option value="formal">Formal (standard text)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Instructions (optional)
              </label>
              <textarea
                name="customInstructions"
                value={formData.customInstructions}
                onChange={handleInputChange}
                rows={2}
                placeholder="Any specific requirements for this worksheet..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </>
        );
        
      case 'progress_sheet':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Weeks
              </label>
              <select
                name="weekCount"
                value={formData.weekCount}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="4">4 weeks</option>
                <option value="6">6 weeks (recommended)</option>
                <option value="8">8 weeks</option>
                <option value="12">12 weeks (half term)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Title (optional)
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={`${activity.title} - Progress Tracker`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {targets.length > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Targets to track:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {targets.slice(0, 4).map((target, i) => (
                    <li key={target.id} className="flex items-start gap-2">
                      <Icons.Target className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                      <span>{target.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        );
        
      case 'story':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Story Theme <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="theme"
                value={formData.theme}
                onChange={handleInputChange}
                placeholder="e.g., Going to the Beach, Rainy Day, Making Friends"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sensory Elements (comma-separated)
              </label>
              <input
                type="text"
                name="sensoryElements"
                value={formData.sensoryElements}
                onChange={handleInputChange}
                placeholder="e.g., water, sand, sun, waves"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                These will become props for the sensory story
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Communication Level
              </label>
              <select
                name="communicationLevel"
                value={formData.communicationLevel}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="pre-formal">Pre-formal (objects of reference)</option>
                <option value="semi-formal">Semi-formal (simple sentences)</option>
                <option value="formal">Formal (narrative text)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Format
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    value="print"
                    checked={formData.format === 'print'}
                    onChange={handleInputChange}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Print (A4)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    value="whiteboard"
                    checked={formData.format === 'whiteboard'}
                    onChange={handleInputChange}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">Whiteboard (16:9)</span>
                </label>
              </div>
            </div>
          </>
        );
        
      case 'visual_support':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Support Type
              </label>
              <select
                name="supportType"
                value={formData.supportType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="comic_strip">Comic Strip Conversation</option>
                <option value="social_story">Social Story</option>
                <option value="visual_schedule">Visual Schedule</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Scenario <span className="text-red-500">*</span>
              </label>
              <textarea
                name="scenario"
                value={formData.scenario}
                onChange={handleInputChange}
                rows={3}
                placeholder={
                  formData.supportType === 'comic_strip'
                    ? "e.g., A student accidentally bumps into someone in the corridor..."
                    : formData.supportType === 'social_story'
                    ? "e.g., Going to a new classroom for the first time..."
                    : "e.g., Morning routine when arriving at school..."
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Student Context (optional)
              </label>
              <input
                type="text"
                name="studentContext"
                value={formData.studentContext}
                onChange={handleInputChange}
                placeholder="e.g., Student has difficulty understanding sarcasm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {formData.supportType === 'comic_strip' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Panels
                </label>
                <select
                  name="panelCount"
                  value={formData.panelCount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="3">3 panels</option>
                  <option value="4">4 panels</option>
                  <option value="5">5 panels</option>
                  <option value="6">6 panels</option>
                </select>
              </div>
            )}
          </>
        );
        
      default:
        return null;
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="div"
                  className="flex items-center justify-between mb-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                      <Icons.Download className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Generate {getDownloadTypeName(downloadType)}
                    </h3>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icons.XMark className="w-5 h-5" />
                  </button>
                </Dialog.Title>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Activity:</span> {activity.title}
                  </p>
                </div>

                <div className="space-y-4">
                  {renderFormFields()}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    disabled={isGenerating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Icons.Sparkles className="w-4 h-4" />
                        Generate PDF
                      </>
                    )}
                  </button>
                </div>

                <p className="mt-4 text-xs text-gray-500 text-center">
                  Generated PDFs use SEN Curriculum branding and are saved to your school's storage.
                </p>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GenerateResourceModal;



